import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/packages/paste-website/src/layouts/DefaultLayout.tsx";
import { graphql } from 'gatsby';
export const pageQuery = graphql`
  {
    mdx(fields: {slug: {eq: "/getting-started/about-paste/"}}) {
      fileAbsolutePath
      frontmatter {
        slug
        title
      }
      headings {
        depth
        value
      }
    }
  }
`;
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Paragraph = makeShortcode("Paragraph");
const PageAside = makeShortcode("PageAside");
const layoutProps = {
  pageQuery,
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">




    <content>
      <h1>{props.pageContext.frontmatter.title}</h1>
      <Paragraph mdxType="Paragraph">{props.pageContext.frontmatter.description}</Paragraph>
    </content>
    <hr></hr>
    <contentwrapper>
      <PageAside data={props.data.mdx} mdxType="PageAside" />
      <content>
        <h2>{`Overview and key concepts`}</h2>
        <p>{`Paste is a design system used to build accessible, cohesive, and high-quality customer experiences at Twilio. Paste supports Product Designers and Engineers with tooling and assets that help them build customer UIs in Sketch and React.`}</p>
        <h3>{`Glossary of terms`}</h3>
        <table>
          <thead parentName="table">
            <tr parentName="thead">
              <th parentName="tr" {...{
                "align": null
              }}>{`Term`}</th>
              <th parentName="tr" {...{
                "align": null
              }}>{`Description`}</th>
            </tr>
          </thead>
          <tbody parentName="table">
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`Paste`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`This design system`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}>{`DSYS`}</td>
              <td parentName="tr" {...{
                "align": null
              }}>{`The team that makes this design system`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/tokens"
                }}>{`Tokens`}</a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Named entities that store visual design attributes`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/primitives"
                }}>{`Primitives`}</a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Unstyled, accessible UI interaction or behavior that can be used as a starting point for specific UI needs`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/components"
                }}>{`Components`}</a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`A suite of common UI components (in Sketch and React) that can be composed into any web based user experience. They follow the WCAG 2.1 AA standard and the W3C ARIA authoring practices to ensure the best accessibility practices are baked into them. They are available to be installed via NPM or Abstract for anyone to use.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/tokens/theme-package"
                }}>{`Themes`}</a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Globally scoped variations of the design language used by Tokens (and subsequently by Components and Compositions). We use Themes to provide a transitional tool for products to switch their UI from older languages to Paste. In the future, themes may also be used to provide light and dark modes for UIs, or for Twilio customers to match their brand personality while still retaining Paste interaction patterns.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/layout"
                }}>{`Layouts`}</a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Ways to responsively space, align, and position your UI.`}</td>
            </tr>
            <tr parentName="tbody">
              <td parentName="tr" {...{
                "align": null
              }}><a parentName="td" {...{
                  "href": "/patterns"
                }}>{`Patterns`}</a></td>
              <td parentName="tr" {...{
                "align": null
              }}>{`Common, repeatable, and extensible Twilio UX compositions that you can use to solve product and UI problems.`}</td>
            </tr>
          </tbody>
        </table>
        <h2>{`Why are we building Paste?`}</h2>
        <p>{`A common question we get is: why build a design system at Twilio? Here are some of the reasons why we`}{`’`}{`re investing in a common UX platform for our customers:`}</p>
        <h3>{`Accessibility at Twilio scale`}</h3>
        <p>{`One of the core components of Twilio Magic is to Be Inclusive. Designing and building experiences that are accessible to a diverse set of users is an important part of this goal, but unfortunately requires a great deal of time and subject matter expertise. Teams that use Paste get accessibility best practices built in, so that they can focus on the customer problem without sacrificing inclusion.`}</p>
        <h3>{`Bring cohesion to our diverse suite of products`}</h3>
        <p>{`Great UX is important, and equally so is the unified application of it. Twilio customers use different products for different needs. By using Paste components and guidelines that are aligned to Twilio UX’s best practice, teams can swiftly build products that customers don’t have to relearn constantly. And for specific customer needs, Paste also allows for thoughtful deviation via custom implementations to truly delight users.`}</p>
        <h3>{`Get better at scale and optimize for efficiency that counts`}</h3>
        <p>{`By providing all teams with a shared set of composable tokens, components, and patterns to pull from, Paste helps teams design and build faster, and makes future UI upgrades simple. This allows us to keep our product quality bar high without sacrificing speed. This also allows for efficient reuse of features and designs across a customer’s journey by enabling UI reuse across different products and workflows.`}</p>
        <h3>{`Instill trust and confidence`}</h3>
        <p>{`Paste is designed to mature our UI interfaces and improve reuse, resiliency, and upgrades at scale. By using Paste, we can create interfaces that inspire customer trust by shipping product UI updates with zero negative impact to the end customer. Our technology choices mean our components are encapsulated but extensible, enabling versioned design updates.`}</p>
        <h2>{`Paste is...`}</h2>
        <ul>
          <li parentName="ul"><strong parentName="li">{`Accessible by default.`}</strong>{` As a principle, the design systems team does not ship a component, primitive, or composition if it does not meet or surpass our target of WCAG 2.1 AA Compliance. This means that as a consuming team, you can expect a lot of the heavy lifting required to build accessible products to be done by Paste. However, you must still think about, and regularly audit your product for accessibility. The Design Systems team can support you on this work.`}</li>
          <li parentName="ul"><strong parentName="li">{`Composable by design.`}</strong>{` Paste tokens, primitives, components, and compositions themselves are inherently composable. This means, in the simplest of terms, that you can use small or large pieces interchangably to solve your product / UI problems.`}</li>
        </ul>
        <h2>{`Paste is not...`}</h2>
        <ul>
          <li parentName="ul"><strong parentName="li">{`Concerned with application logic.`}</strong>{` All Paste components are presentational, and have no opinions on the way data is passed to them.`}</li>
          <li parentName="ul"><strong parentName="li">{`Stateful.`}</strong>{` While components have encapsulated internal state when necessary (e.g.UI behavior or keyboard interaction), the components themselves are not concerned with application state.`}</li>
          <li parentName="ul"><strong parentName="li">{`Product or domain specific.`}</strong>{` Paste components aim to serve all UI needs semantically, and Paste compositions aim to serve Twilio pattern needs at the most generic level. We believe that a good design system raises the floor of great customer experience, and are excited to see thoughtful, well-researched ejections from the system that raise the ceiling.`}</li>
        </ul>
        <h2>{`Inspired by...`}</h2>
        <p>{`Paste takes a lot of its inspiration from the fantastic folks that have done this before us. We seek diverse opinions in designing our solutions for our customers. We can`}{`’`}{`t build Paste without the ideas and tools proposed by:`}</p>
        <ul>
          <li parentName="ul">{`Jina Anne and Jon Levine for `}<a parentName="li" {...{
              "href": "https://www.youtube.com/watch?v=wDBEc3dJJV8"
            }}>{`Design Tokens`}</a></li>
          <li parentName="ul">{`Brent Jackson for `}<a parentName="li" {...{
              "href": "https://medium.com/@jxnblk/patterns-for-style-composition-in-react-eb4f5f313e6f"
            }}>{`Box and Style props`}</a></li>
          <li parentName="ul">{`Segun Adebayo for `}<a parentName="li" {...{
              "href": "https://chakra-ui.com/"
            }}>{`Chakra-UI`}</a></li>
          <li parentName="ul">{`Diego Haz for `}<a parentName="li" {...{
              "href": "https://reakit.io/"
            }}>{`Reakit `}</a></li>
          <li parentName="ul">{`SuperFriendly for `}<a parentName="li" {...{
              "href": "https://superfriendlydesign.systems/tools/icons/"
            }}>{`Design Systems Icons`}</a></li>
        </ul>
      </content>
    </contentwrapper>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      